import { FC, FocusEventHandler, memo, useCallback, useState } from 'react'
import { TextField } from '@microservices/wiskey-react-components'
import { Box } from '@mui/material'
import { Cell } from '@tanstack/react-table'

import { generalFieldStylesByMode } from '@helpers'
import { MAX_VIEW_COLUMN_WIDTH, MIN_VIEW_COLUMN_WIDTH } from '@constants'
import { FormattedColumnRestrictions, RESTRICTION_TYPE, RestrictionDTO } from '@types'

interface WidthCellProps {
  cell: Cell<FormattedColumnRestrictions, unknown>
  updateRestrictions: (restrictions: RestrictionDTO[]) => Promise<unknown>
  viewId: number
}

const WidthCellComponent: FC<WidthCellProps> = ({ cell, updateRestrictions, viewId }) => {
  const columnRestrictions = cell.row.original
  const restriction = columnRestrictions.width
  const [value, setValue] = useState<string>(
    restriction?.value?.toString() || MIN_VIEW_COLUMN_WIDTH.toString()
  )

  const handleWidthChange = (fieldCode: string, fieldName: string, newValue: string) => {
    updateRestrictions([
      {
        viewId,
        fieldCode,
        fieldName,
        restrictionType: RESTRICTION_TYPE.WIDTH,
        value: newValue,
      },
    ]).catch(error => {
      console.error('Error updating width restriction:', error)
    })
  }

  const getValue = (value: string, initValue: string) => {
    let newValue

    if (value === '') {
      return MIN_VIEW_COLUMN_WIDTH.toString()
    }

    if (!isNaN(parseInt(value, 10))) {
      let intValue = parseInt(value, 10)
      if (intValue > MAX_VIEW_COLUMN_WIDTH) intValue = MAX_VIEW_COLUMN_WIDTH
      if (intValue < MIN_VIEW_COLUMN_WIDTH) intValue = MIN_VIEW_COLUMN_WIDTH
      newValue = intValue.toString()
    } else {
      newValue = initValue
    }

    return newValue
  }

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value
    setValue(newValue)
  }, [])

  const handleSaveValue = useCallback(() => {
    const newValue = getValue(
      value,
      restriction?.value?.toString() || MIN_VIEW_COLUMN_WIDTH.toString()
    )
    setValue(newValue)
    if (
      !(newValue === MIN_VIEW_COLUMN_WIDTH.toString() && !restriction?.value) &&
      newValue !== restriction?.value?.toString()
    ) {
      handleWidthChange(columnRestrictions.fieldCode, columnRestrictions.fieldName, newValue)
    }
  }, [value, restriction?.value])

  const handleBlur: FocusEventHandler<HTMLInputElement> = _event => {
    handleSaveValue()
  }

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        event.key === 'Enter' ||
        event.key === 'Backspace' ||
        event.key === 'Delete' ||
        event.key === 'Escape' ||
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowRight' ||
        event.key === 'ArrowUp' ||
        event.key === 'ArrowDown' ||
        // Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (event.ctrlKey &&
          (event.key === 'a' || event.key === 'c' || event.key === 'v' || event.key === 'x'))
      ) {
        if (event.key === 'Enter') {
          event.preventDefault()
          handleSaveValue()
        }

        return
      }

      if (!/^\d$/.test(event.key)) {
        event.preventDefault()
      }
    },
    [handleSaveValue]
  )

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <TextField
        fullWidth
        size={'small'}
        type='text'
        value={value}
        InputProps={{
          sx: {
            paddingRight: 2,
            boxSizing: 'border-box',
          },
          onBlur: handleBlur,
          onKeyDown: handleKeyDown,
          onChange: handleChange,
          value,
        }}
        inputProps={{
          maxLength: 4,
        }}
        sx={theme => ({
          ...generalFieldStylesByMode(theme, undefined, undefined, undefined, true),
        })}
      />
    </Box>
  )
}

const arePropsEqual = (prevProps: WidthCellProps, nextProps: WidthCellProps) => {
  return (
    prevProps.viewId === nextProps.viewId &&
    prevProps.cell.row.original.fieldCode === nextProps.cell.row.original.fieldCode &&
    prevProps.cell.row.original.width?.value === nextProps.cell.row.original.width?.value
  )
}

export const WidthCell = memo(WidthCellComponent, arePropsEqual)
